import axios from 'axios';
import humps from 'humps';
import * as Sentry from '@sentry/react';
import { clearStorage } from './session';
import { env } from './env';

// The pod's runtime config (/env-config.js) first, the Vite env in `npm run dev`.
export const domain =
  env('VITE_API_URL') || `${window.location.protocol}//${window.location.hostname}`;

//  Add Base URL and change snake_case to camelCase
const baseAxios = axios.create({
  baseURL: `${domain}`,
  transformResponse: [...axios.defaults.transformResponse, humps.camelizeKeys],
  transformRequest: [decamelize, ...axios.defaults.transformRequest],
});

// Request Interceptor
baseAxios.interceptors.request.use((config) => ({
  ...config,
  params: humps.decamelizeKeys(config.params),
}));

// Response Interceptor for error logging
baseAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - clear localStorage and redirect
    if (error.response?.status === 401) {
      clearStorage();
    }

    // Log to Sentry for debugging
    const errorInfo = {
      message: error.message,
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status || 'Network Error',
      data: error.response?.data || null,
    };

    Sentry.withScope((scope) => {
      scope.setTag('http.method', error.config?.method);
      scope.setTag('http.status', error.response?.status || 'Network Error');
      scope.setContext('API Error', errorInfo);
      Sentry.captureException(error);
    });

    // Reject with the original axios error so e.response is preserved
    return Promise.reject(error);
  }
);

export default baseAxios;

// Public axios instance — no auth headers, no clearStorage on 401
export const publicRequest = axios.create({
  baseURL: `${domain}`,
  transformResponse: [...axios.defaults.transformResponse, humps.camelizeKeys],
  transformRequest: [decamelize, ...axios.defaults.transformRequest],
});

publicRequest.interceptors.request.use((config) => ({
  ...config,
  params: humps.decamelizeKeys(config.params),
}));

// Helper function for decamelizing keys in requests
function decamelize(object) {
  if (!(object && !(object instanceof File))) return object;

  if (object instanceof FormData) {
    const formData = new FormData();
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of object.entries()) {
      formData.append(humps.decamelize(key), value);
    }
    return formData;
  }

  if (typeof object === 'object') {
    return humps.decamelizeKeys(object);
  }

  // Add a default return value to satisfy ESLint
  return object;
}
